import React from 'react';
import { cn } from '../../lib/utils';
import { ChevronIcon } from './icons';

const ARROW =
  'flex shrink-0 items-center justify-center text-root-text transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
/** Phone: 48px round buttons on a soft tray disc, so the whole hit area reads as tappable. */
const ARROW_SM = 'h-12 w-12 rounded-full bg-tray/60 active:bg-tray lp:h-10 lp:w-10';
/** Desktop: 44px, no disc until hover. */
const ARROW_MD = 'h-11 w-11 rounded-[6px] hover:bg-tile/50';

/**
 * Page title ("G Major Scale") with ‹ › arrow buttons that step through keys.
 * The title is an <h1>, so use one KeyTitle per page.
 *
 * Sizes follow the boards, chosen by prop so the layout owns the phone/desktop switch:
 *   'sm' (default, board 18): 24px title, 22px chevrons in 48px round buttons on a soft disc
 *        (44px felt small on a phone), arrows pushed to the row edges. A phone held sideways
 *        (`lp:`) gets 40px arrows and a 20px title so the board fits under them.
 *   'md' (board 20): 36px title, 20px chevrons in 44px buttons, centred with a 12px gap.
 *
 * @param {object} props
 * @param {string} props.title
 * @param {() => void} props.onPrev
 * @param {() => void} props.onNext
 * @param {string} [props.prevLabel] Accessible name for the ‹ button.
 * @param {string} [props.nextLabel] Accessible name for the › button.
 * @param {'sm'|'md'} [props.size]
 * @param {string} [props.className]
 */
export function KeyTitle({
  title,
  onPrev,
  onNext,
  prevLabel = 'Previous key',
  nextLabel = 'Next key',
  size = 'sm',
  className,
}) {
  const md = size === 'md';
  const chevron = md ? 20 : 22;
  const arrow = cn(ARROW, md ? ARROW_MD : ARROW_SM);
  return (
    <div
      className={cn(
        'flex items-center',
        md ? 'justify-center gap-3' : 'w-full justify-between gap-2 px-2',
        className,
      )}
    >
      <button type="button" aria-label={prevLabel} onClick={onPrev} className={arrow}>
        <ChevronIcon direction="left" size={chevron} />
      </button>
      <h1 className={cn('m-0 min-w-0 text-center font-bold tracking-[-0.01em] text-ink', md ? 'text-4xl' : 'text-2xl lp:text-xl')}>
        {title}
      </h1>
      <button type="button" aria-label={nextLabel} onClick={onNext} className={arrow}>
        <ChevronIcon direction="right" size={chevron} />
      </button>
    </div>
  );
}
